import { useCallback, useEffect, useState } from 'react';
import { Marker, InfoWindow } from '@react-google-maps/api';
import { CommanServices } from '@/services/commanServices';
import socketService from '@/services/socketService';

// Live "assigned personnel" layer — shared by the incident-detail map widget
// (MyMap) and the Full/Create Map page (ExerciseMapPage) so both always show
// the same real-time picture of everyone staffed on the incident.

export function useIncidentPersonnel(incidentId) {
  const [personnel, setPersonnel] = useState([]);

  const fetchPersonnel = useCallback(async () => {
    if (!incidentId) return;
    try {
      const res = await CommanServices.getIncidentAssignedPersonnel(incidentId);
      if (res.status) setPersonnel(Array.isArray(res.data) ? res.data : []);
    } catch { /* personnel markers simply don't render */ }
  }, [incidentId]);

  useEffect(() => { fetchPersonnel(); }, [fetchPersonnel]);

  useEffect(() => {
    if (!incidentId) return undefined;
    // New assignments arrive as incident sync; movements as live location pings.
    const offSync = socketService.on('receive_incident_sync', (d) => {
      if (String(d?.incident_id || '') === String(incidentId)) fetchPersonnel();
    });
    const offLoc = socketService.on('team_location_updated', (d) => {
      if (!d?.user_id) return;
      if (String(d?.assigned_incident_id || '') === String(incidentId)) fetchPersonnel();
      if (d.lat == null || d.lng == null) return;
      setPersonnel((prev) => prev.map((p) => (
        String(p.user_id) === String(d.user_id)
          ? { ...p, lat: d.lat, lng: d.lng, status: d.status || p.status, timestamp: d.timestamp || p.timestamp }
          : p
      )));
    });
    return () => { offSync(); offLoc(); };
  }, [incidentId, fetchPersonnel]);

  return personnel;
}

export function PersonnelMarkers({ personnel }) {
  const [openId, setOpenId] = useState(null);
  const pins = (personnel || []).filter((p) => p.lat != null && p.lng != null);

  return pins.map((p) => (
    <Marker
      key={`person-${p.user_id}`}
      position={{ lat: Number(p.lat), lng: Number(p.lng) }}
      onClick={() => setOpenId(p.user_id)}
      title={`${p.user_name} — ${p.assigned_role}`}
      icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: '#10B981', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 }}
    >
      {openId === p.user_id && (
        <InfoWindow position={{ lat: Number(p.lat), lng: Number(p.lng) }} onCloseClick={() => setOpenId(null)}>
          <div className="p-1 min-w-[160px]" data-testid={`personnel-info-${p.user_id}`}>
            <p className="text-xs font-bold text-gray-900">{p.user_name}</p>
            <p className="text-[11px] text-gray-500">{p.assigned_role}{p.department_unit ? ` · ${p.department_unit}` : ''}</p>
            {p.timestamp && (
              <p className="text-[10px] text-gray-400 mt-0.5">Last seen {new Date(p.timestamp).toLocaleTimeString()}</p>
            )}
          </div>
        </InfoWindow>
      )}
    </Marker>
  ));
}
